/**
 * Supervisor monitor controller — framework-agnostic state machine for live
 * call monitoring (listen / whisper / barge).
 *
 * This module owns ALL monitoring state and the apex-voice command protocol
 * (cmd.supervisor_*). It is deliberately UI-free: the React panel
 * (client/src/components/voice/SupervisorMonitor.tsx) subscribes via
 * `subscribe()` + `getSnapshot()` (useSyncExternalStore) and renders; this
 * module never touches the DOM.
 *
 * It is also the single source of truth for the SDK-call interception that
 * `softphone.ts` relies on. When a supervisor joins a conference, apex-voice
 * tells Twilio to dial the supervisor's SDK client identity; the resulting
 * incoming SDK call must be auto-accepted rather than shown as an inbound
 * call. `softphone.ts` calls `isAwaitingSupervisorJoin()` on every SDK
 * incoming and, when true, hands the call to `receiveSupervisorSdkCall()`.
 * Those two exports keep the exact signatures the old vanilla
 * `supervisor-panel.ts` exposed, so `softphone.ts` is unchanged beyond its
 * import path.
 *
 * Replaces the legacy vanilla `client/lib/supervisor-panel.ts`, which built
 * its own DOM and only ran in the retired vanilla shell.
 */

import {
  request as voiceRequest,
  on as voiceOn,
  isConnected as isVoiceConnected,
} from './voice-ws';
import { ensureWebRtc } from './softphone';
import { trace, debugEmit } from './debug-stream';
import type { WebRtcCall } from './webrtc';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SupervisorCallInfo {
  id: string;
  tenant_id: string;
  agent_id: string;
  direction: string;
  from_number: string;
  to_number: string;
  status: string;
  started_at: string;
  answered_at?: string;
  conference_name: string;
}

export type SupervisorMode = 'listen' | 'whisper' | 'barge';

export interface MonitorSession {
  callId: string;
  mode: SupervisorMode;
  callSid: string;
  conferenceName: string;
}

/**
 * Immutable snapshot consumed by React via useSyncExternalStore. A new object
 * identity is produced on every state change (and only then) so React's
 * referential-equality check fires exactly when something the UI cares about
 * moved.
 */
export interface MonitorSnapshot {
  activeCalls: SupervisorCallInfo[];
  monitorSession: MonitorSession | null;
  joining: boolean;
  awaitingJoin: boolean;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Roles that may monitor calls. Mirrors the auth role hierarchy. */
export const SUPERVISOR_ROLES = new Set([
  'supervisor',
  'manager',
  'admin',
  'csuite',
  'platform-ops',
]);

const ACTIVE_CALLS_POLL_MS = 10_000;
const SDK_JOIN_TIMEOUT_MS = 15_000;

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

let activeCalls: SupervisorCallInfo[] = [];
let monitorSession: MonitorSession | null = null;
let supervisorSdkCall: WebRtcCall | null = null;
let awaitingJoin = false;
let joining = false;

/** The current user's id (users.id / JWT sub), used to match WS events. */
let identitySub: string | null = null;

let voiceEventsRegistered = false;
let pollTimer: ReturnType<typeof setInterval> | null = null;
let panelOpen = false;

// useSyncExternalStore plumbing
const listeners = new Set<() => void>();
let snapshot: MonitorSnapshot = {
  activeCalls,
  monitorSession,
  joining,
  awaitingJoin,
};

function emit(): void {
  snapshot = { activeCalls, monitorSession, joining, awaitingJoin };
  for (const l of listeners) l();
}

// ---------------------------------------------------------------------------
// useSyncExternalStore API
// ---------------------------------------------------------------------------

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getSnapshot(): MonitorSnapshot {
  return snapshot;
}

/** Record the current user's id so WS events can be matched to this session. */
export function setMonitorIdentity(sub: string | null): void {
  identitySub = sub;
}

// ---------------------------------------------------------------------------
// Softphone interception (consumed by softphone.ts — keep signatures stable)
// ---------------------------------------------------------------------------

/** Whether a supervisor join is awaiting its auto-accept SDK incoming. */
export function isAwaitingSupervisorJoin(): boolean {
  return awaitingJoin;
}

/** Receive the incoming SDK call triggered by a supervisor join. Auto-accepts. */
export function receiveSupervisorSdkCall(call: WebRtcCall): void {
  debugEmit('supervisor', 'received SDK call, auto-accepting', { callSid: call.callSid });
  call.accept();
  supervisorSdkCall = call;
  awaitingJoin = false;

  call.onStateChange((state) => {
    if (state === 'disconnected') {
      debugEmit('supervisor', 'SDK call disconnected');
      supervisorSdkCall = null;
      if (monitorSession) {
        monitorSession = null;
      }
      emit();
    }
  });

  emit();
}

// ---------------------------------------------------------------------------
// Voice event registration
// ---------------------------------------------------------------------------

/**
 * Register the voice WS listeners once. Active-call refresh events only
 * trigger a fetch while the panel is open; supervisor.* events are always
 * honored so a session torn down server-side (or by another tab) clears here
 * too.
 */
export function registerVoiceEvents(): void {
  if (voiceEventsRegistered) return;
  voiceEventsRegistered = true;

  const refreshEvents = ['call.ringing', 'call.answered', 'call.ended', 'call.initiated'];
  for (const evt of refreshEvents) {
    voiceOn(evt, () => {
      if (panelOpen) void fetchActiveCalls();
    });
  }

  voiceOn('supervisor.left', (p) => {
    if (identitySub && (p['supervisor_id'] as string) === identitySub && monitorSession) {
      monitorSession = null;
      supervisorSdkCall?.hangup();
      supervisorSdkCall = null;
      emit();
    }
  });

  voiceOn('supervisor.mode_changed', (p) => {
    if (identitySub && (p['supervisor_id'] as string) === identitySub && monitorSession) {
      monitorSession = { ...monitorSession, mode: p['mode'] as SupervisorMode };
      emit();
    }
  });
}

// ---------------------------------------------------------------------------
// Panel open/close — controls active-call polling
// ---------------------------------------------------------------------------

export function openPanel(): void {
  if (panelOpen) return;
  panelOpen = true;
  void fetchActiveCalls();
  pollTimer = setInterval(() => void fetchActiveCalls(), ACTIVE_CALLS_POLL_MS);
}

export function closePanel(): void {
  panelOpen = false;
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

// ---------------------------------------------------------------------------
// Data fetching
// ---------------------------------------------------------------------------

export async function fetchActiveCalls(): Promise<void> {
  if (!isVoiceConnected()) return;
  try {
    const result = await voiceRequest<{ calls: SupervisorCallInfo[] }>('cmd.supervisor_calls');
    activeCalls = result.calls || [];
    emit();
  } catch (err) {
    debugEmit('supervisor', 'fetch calls failed', { error: String(err) }, 'warn');
  }
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

export async function joinCall(callId: string, mode: SupervisorMode): Promise<void> {
  if (joining || monitorSession) return;
  joining = true;
  awaitingJoin = true;
  emit();

  const cmdMap: Record<SupervisorMode, string> = {
    listen: 'cmd.supervisor_listen',
    whisper: 'cmd.supervisor_whisper',
    barge: 'cmd.supervisor_barge',
  };

  const t = trace('supervisor', `join-${mode}`, { callId });
  try {
    await ensureWebRtc();

    const result = await voiceRequest<{
      status: string;
      mode: string;
      call_sid: string;
      conference_name: string;
    }>(cmdMap[mode], { call_id: callId });

    monitorSession = {
      callId,
      mode,
      callSid: result.call_sid,
      conferenceName: result.conference_name,
    };
    t.ok({ callSid: result.call_sid });

    // If the SDK call never arrives, clear the awaiting flag so the panel
    // stops showing a connecting spinner forever.
    setTimeout(() => {
      if (awaitingJoin) {
        awaitingJoin = false;
        debugEmit('supervisor', 'SDK call timeout', undefined, 'warn');
        emit();
      }
    }, SDK_JOIN_TIMEOUT_MS);
  } catch (err) {
    awaitingJoin = false;
    monitorSession = null;
    t.fail(err);
  } finally {
    joining = false;
    emit();
  }
}

export async function changeMode(mode: SupervisorMode): Promise<void> {
  if (!monitorSession || monitorSession.mode === mode) return;
  const t = trace('supervisor', 'change-mode', { callId: monitorSession.callId, mode });
  try {
    await voiceRequest('cmd.supervisor_mode', { call_id: monitorSession.callId, mode });
    monitorSession = { ...monitorSession, mode };
    emit();
    t.ok();
  } catch (err) {
    t.fail(err);
  }
}

export async function leaveCall(): Promise<void> {
  if (!monitorSession) return;
  const t = trace('supervisor', 'leave', { callId: monitorSession.callId });
  try {
    await voiceRequest('cmd.supervisor_leave', { call_id: monitorSession.callId });
    t.ok();
  } catch (err) {
    t.fail(err);
  }
  supervisorSdkCall?.hangup();
  supervisorSdkCall = null;
  monitorSession = null;
  emit();
}

// ---------------------------------------------------------------------------
// Test-only reset (no production caller)
// ---------------------------------------------------------------------------

export function __resetForTest(): void {
  activeCalls = [];
  monitorSession = null;
  supervisorSdkCall = null;
  awaitingJoin = false;
  joining = false;
  identitySub = null;
  voiceEventsRegistered = false;
  panelOpen = false;
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  listeners.clear();
  emit();
}
